/**
 * Preview of the `services_url_slug` the backend derives from an org title.
 *
 * Mirrors `services_slugify` in api-rs `post_org_bootstrap.rs`, and is kept in
 * sync by
 * hand — which is safe only because this is *only* a preview. The backend
 * derives the value that is actually stored and owns the uniqueness check, so a
 * drift here shows a wrong hint rather than writing a wrong slug.
 *
 * See slugPreview.test.ts: the expectations there are real title/slug pairs taken
 * from the database, so drift from the Rust rules fails a test rather than
 * quietly misleading whoever is onboarding an agency.
 */
export const slugPreview = (title: string): string => {
  let s = "";
  // Apostrophes are dropped rather than becoming a separator, matching every
  // existing slug: "Douglas County Sheriff's Office" is
  // douglas-county-sheriffs-office. (The Stytch slug, which shares the rest of
  // these rules, does turn them into a separator.)
  for (const c of title.toLowerCase().replace(/'/g, "")) {
    // Non-ASCII is dropped rather than transliterated, matching the Rust side
    // (Stytch rejects accented characters).
    if (c.charCodeAt(0) > 127) continue;
    if (c === " " || c === "/") s += "-";
    else if (c === "&") s += "and";
    else if (/[a-z0-9\-._~]/.test(c)) s += c;
  }
  while (s.includes("--")) s = s.replace("--", "-");
  const trimmed = s.replace(/^-+|-+$/g, "");
  // The Rust version pads slugs under two characters so Stytch's minimum length
  // is met; an empty title stays empty so callers can treat it as "nothing yet".
  return trimmed.length > 0 && trimmed.length < 2 ? `${trimmed}-org` : trimmed;
};
